/**
 * Admin API client — all /api/admin/* calls.
 * Only used by AdminPanel. Requires drfahm_admin JWT.
 */

const BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

function getToken() {
  return localStorage.getItem('access_token');
}

async function adminRequest(path, options = {}) {
  const token   = getToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { ...options, headers });

  // For CSV/PDF downloads — return raw response
  if (options._raw) return res;

  let body;
  try { body = await res.json(); } catch { body = {}; }

  if (!res.ok) throw { status: res.status, ...body };
  return body;
}

/**
 * Multipart form upload — used for CSV bulk operations.
 * Does NOT set Content-Type (browser sets it with boundary).
 */
async function adminUpload(path, formData, queryParams = '') {
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const url = `${BASE}${path}${queryParams ? '?' + queryParams : ''}`;
  const res = await fetch(url, { method: 'POST', headers, body: formData });

  let body;
  try { body = await res.json(); } catch { body = {}; }

  if (!res.ok) throw { status: res.status, ...body };
  return body;
}

// ── Stats ────────────────────────────────────────────────────────────────────
export const getStats = () => adminRequest('/api/admin/stats');

// Chunk 24: read-only growth metrics for the Stats tab Growth card.
// Returns { generated_at, windows, new_registrations, entitlements_activated,
//           active_students_7d, per_exam }. "today" = KSA calendar day (UTC+3).
export const getAdminMetrics = () => adminRequest('/api/admin/metrics');

// ── Topics ───────────────────────────────────────────────────────────────────
export const getTopics = (section) =>
  adminRequest(`/api/admin/topics${section ? '?section=' + section : ''}`);

export const topicCoverage = (params = {}) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null))
  ).toString();
  return adminRequest(`/api/admin/questions/topic-coverage${qs ? '?' + qs : ''}`);
};

// ── Questions ────────────────────────────────────────────────────────────────
export const listQuestions = (params = {}) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null))
  ).toString();
  return adminRequest(`/api/admin/questions${qs ? '?' + qs : ''}`);
};

export const getQuestion     = (id)       => adminRequest(`/api/admin/questions/${id}`);
export const updateQuestion  = (id, data) => adminRequest(`/api/admin/questions/${id}`, { method: 'PUT',    body: JSON.stringify(data) });
export const deleteQuestion  = (id)       => adminRequest(`/api/admin/questions/${id}`, { method: 'DELETE' });
export const toggleQuestion  = (id, is_active) => adminRequest(`/api/admin/questions/${id}/activate`, { method: 'PATCH', body: JSON.stringify({ is_active }) });
export const importQuestions = (arr)      => adminRequest('/api/admin/questions/import', { method: 'POST', body: JSON.stringify(arr) });
export const nextIndex       = (exam, world_key) => adminRequest(`/api/admin/questions/next-index?exam=${exam}&world_key=${world_key}`);
export const bulkActivate    = (data)     => adminRequest('/api/admin/questions/bulk-activate', { method: 'POST', body: JSON.stringify(data) });
export const bulkTopic       = (data)     => adminRequest('/api/admin/questions/bulk-topic',    { method: 'POST', body: JSON.stringify(data) });
export const bulkLanguage    = (data)     => adminRequest('/api/admin/questions/bulk-language', { method: 'POST', body: JSON.stringify(data) });
export const reviewProgress  = (exam)     => adminRequest(`/api/admin/questions/review-progress${exam ? '?exam=' + exam : ''}`);
export const markReviewed    = (id, version) => adminRequest(`/api/admin/questions/${id}/mark-reviewed`, { method: 'PATCH', body: JSON.stringify({ version }) });

export const quickUpdate = (id, field, value, version) =>
  adminRequest(`/api/admin/questions/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ [field]: value, version }),
  });

// ── Bulk Delete ──────────────────────────────────────────────────────────────
export const bulkDelete = (questionIds) =>
  adminRequest('/api/admin/questions/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ question_ids: questionIds, confirm: true }),
  });

// ── Bulk Assign ──────────────────────────────────────────────────────────────
export const bulkAssign = (questionIds, assign) =>
  adminRequest('/api/admin/questions/bulk-assign', {
    method: 'POST',
    body: JSON.stringify({ question_ids: questionIds, assign }),
  });

// ── Bulk CSV Upload ──────────────────────────────────────────────────────────
export const bulkTemplate = () =>
  adminRequest('/api/admin/questions/bulk-template', { _raw: true });

export const bulkValidate = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return adminUpload('/api/admin/questions/bulk-validate', fd);
};

export const bulkCommit = (file, forceDuplicates = false) => {
  const fd = new FormData();
  fd.append('file', file);
  const qs = forceDuplicates ? 'force_duplicates=true' : '';
  return adminUpload('/api/admin/questions/bulk-commit', fd, qs);
};

// ── Orgs ─────────────────────────────────────────────────────────────────────
export const listOrgs            = (params = {}) => adminRequest('/api/admin/orgs?' + new URLSearchParams(params));
export const createOrg           = (data)        => adminRequest('/api/admin/orgs', { method: 'POST', body: JSON.stringify(data) });
export const getOrg              = (id)          => adminRequest(`/api/admin/orgs/${id}`);
export const createOrgLeader     = (id, data)    => adminRequest(`/api/admin/orgs/${id}/leader`, { method: 'POST', body: JSON.stringify(data) });
export const generateStudents    = (id, data)    => adminRequest(`/api/admin/orgs/${id}/students/generate`, { method: 'POST', body: JSON.stringify(data) });
export const exportStudentsCsv   = (id)          => adminRequest(`/api/admin/orgs/${id}/students/export`, { _raw: true });
export const grantOrgEntitlement = (id, data)    => adminRequest(`/api/admin/orgs/${id}/entitlement`, { method: 'POST', body: JSON.stringify(data) });

// Chunk 23 (white-label teacher dashboard): set or clear an org's branding.
// Partial patch — only keys present change; null or '' clears a field.
// data: { name?: string|null, logo_url?: string|null, primary_hex?: string|null }
// Returns { org } with the new values under org.branding.
export const updateOrgBranding   = (id, data)    => adminRequest(`/api/admin/orgs/${id}/branding`, { method: 'PATCH', body: JSON.stringify(data) });

// ── Users ────────────────────────────────────────────────────────────────────
export const listUsers      = (params = {}) => adminRequest('/api/admin/users?' + new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null))));
export const createUser     = (data)        => adminRequest('/api/admin/users', { method: 'POST', body: JSON.stringify(data) });
export const createReviewer = (data)        => adminRequest('/api/admin/reviewers', { method: 'POST', body: JSON.stringify(data) });
export const activateUser   = (id)          => adminRequest(`/api/admin/users/${id}/activate`,       { method: 'PATCH' });
export const deactivateUser = (id)          => adminRequest(`/api/admin/users/${id}/deactivate`,     { method: 'PATCH' });
export const resetPassword  = (id, data)    => adminRequest(`/api/admin/users/${id}/reset-password`, { method: 'POST', body: JSON.stringify(data) });
// Chunk 42: admin-only username rename. Backend refuses admins (403) and
// case-insensitive collisions (409 username_taken). Returns { user, previous_username, message }.
export const renameUser     = (id, username) => adminRequest(`/api/admin/users/${id}/username`, { method: 'PATCH', body: JSON.stringify({ username }) });

// D1 (teacher dashboard): assign or clear a school student's class.
// class_group: string to assign, null to clear. Backend refuses non-students
// and students without an org (400 invalid_role / no_org).
export const setClassGroup  = (id, class_group) => adminRequest(`/api/admin/users/${id}/class-group`, { method: 'PATCH', body: JSON.stringify({ class_group }) });

// DELETE a user account (students and school_leaders only — not admins).
// Cascades: LevelProgress, WorldProgress, ExamTrial, Entitlement.
export const deleteUser = (id)   => adminRequest(`/api/admin/users/${id}`,  { method: 'DELETE' });
export const deleteOrg  = (id)   => adminRequest(`/api/admin/orgs/${id}`,   { method: 'DELETE' });

// ── AI Review  (Chunk J + K1) ────────────────────────────────────────────────
export const aiReview = (questionIds, overwrite = false) =>
  adminRequest('/api/admin/questions/ai-review', {
    method: 'POST',
    body: JSON.stringify({ question_ids: questionIds, overwrite }),
  });

export const approveReview = (id, body) =>
  adminRequest(`/api/admin/questions/${id}/approve-review`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const rejectReview = (id, version) =>
  adminRequest(`/api/admin/questions/${id}/reject-review`, {
    method: 'PATCH',
    body: JSON.stringify({ version }),
  });

// Bulk-bounce every reviewer_submitted question back to the reviewer's queue
// for a second look. Status flips reviewer_submitted → rejected; the reviewer's
// previous answer/hint/topic are preserved (nothing reset). Pass exam to narrow
// scope; omit it to return ALL pending reviewer submissions across every exam.
// Returns { affected, ids }.
export const returnAllToReviewer = (exam = '') =>
  adminRequest('/api/admin/questions/bulk-return-to-reviewer', {
    method: 'POST',
    body: JSON.stringify(exam ? { exam } : {}),
  });

// M4.5: bulk-approve every ai_reviewed question where the AI's predicted
// answer MATCHES the current correct_answer at/above min_confidence.
// Disagreements and low-confidence rows are left pending for human review.
// Optional filters: { language: 'en'|'ar', exam, min_confidence } (default 0.9).
// Returns { approved, hint_copied, topic_copied, left_low_confidence,
//           left_mismatch, left_no_prediction }.
export const bulkApproveMatching = (data = {}) =>
  adminRequest('/api/admin/questions/bulk-approve-matching', {
    method: 'POST',
    body: JSON.stringify(data),
  });

// ── Translate-and-stage  (N2 — Terra EN→AR twins) ────────────────────────────
// Translates up to 20 APPROVED English questions into Arabic twins staged in
// the reviewer queue. Verbal is structurally refused. overwrite=true
// re-translates existing TRANSLATED twins only (authored AR is never touched).
// Returns { created, updated, failed, skipped: {reason: [ids]}, results }.
export const translateBatch = (questionIds, overwrite = false) =>
  adminRequest('/api/admin/questions/translate-batch', {
    method: 'POST',
    body: JSON.stringify({ question_ids: questionIds, overwrite }),
  });

// ── Claims + Next-for-me  (N3 — parallel admin approval) ─────────────────────
// Advisory soft-claims; version lock still guarantees correctness.
export const claimQuestion = (id) =>
  adminRequest(`/api/admin/questions/${id}/claim`, { method: 'POST' });
export const releaseClaim = (id) =>
  adminRequest(`/api/admin/questions/${id}/release-claim`, { method: 'POST' });
// Atomically dispenses the oldest unclaimed reviewer_submitted question
// (SKIP LOCKED — concurrent admins each get a different one). Filters optional.
// Returns { question } or { question: null } when the queue is empty.
export const claimNext = (filters = {}) =>
  adminRequest('/api/admin/questions/claim-next', {
    method: 'POST', body: JSON.stringify(filters),
  });

// ── Hint generation  (Chunk 39 — question's own language; proposal only) ────
export const generateHint = (id) =>
  adminRequest(`/api/admin/questions/${id}/generate-hint`, { method: 'POST' });

// ── Duplicate Detection  (Chunk K3 · exam+language scoping) ──────────────────
// language: '' = all languages (backend still groups per-language, so EN and AR
// rows can never appear in the same duplicate group); 'en' | 'ar' = filter.
export const findDuplicates = (section, exam = '', language = '') =>
  adminRequest('/api/admin/questions/find-duplicates', {
    method: 'POST',
    body: JSON.stringify({
      section,
      ...(exam ? { exam } : {}),
      ...(language ? { language } : {}),
    }),
  });

// ── World Allocation Tools  (Chunk K2) ───────────────────────────────────────
// language: 'en' | 'ar' — which bank the tab is looking at (absent → en,
// per M3b). Counts, fill % and breakdowns are all scoped to that language.
export const getWorldHealth = (exam = '', language = '') => {
  const qs = new URLSearchParams({ ...(exam ? { exam } : {}), ...(language ? { language } : {}) }).toString();
  return adminRequest(`/api/admin/worlds/health${qs ? '?' + qs : ''}`);
};

// Board chunk: read-only per-slot fill map of one world in one language.
export const getWorldBoard = (worldKey, exam, language = 'en') =>
  adminRequest(`/api/admin/worlds/${worldKey}/board?` + new URLSearchParams({ exam, language }));

// Board/N3: a question + its other-language row at the same slot, both full.
export const getQuestionPair = (id) => adminRequest(`/api/admin/questions/${id}/pair`);

// N7: pull authored questions of one language out of a world's slots
// (uniform-pair prep). Nothing deleted. Verbal+AR refused server-side.
export const unassignAuthored = (worldKey, body) =>
  adminRequest(`/api/admin/worlds/${worldKey}/unassign-authored`, {
    method: 'POST', body: JSON.stringify(body),
  });

export const smartFill = (worldKey, body) =>
  adminRequest(`/api/admin/worlds/${worldKey}/smart-fill`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const clearWorld = (worldKey, body) =>
  adminRequest(`/api/admin/worlds/${worldKey}/clear`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

// ── School Checkout — closer tool  (Chunk L3) ────────────────────────────────

/**
 * POST /api/billing/admin/create-school-checkout
 * Generates a Stripe Checkout URL for a school payment.
 * Called by the sales closer during a phone call.
 *
 * @param {{ org_id, exam, student_count, plan_tier }} data
 * @returns {{ checkout_url, total_sar, price_per_student, student_count,
 *             plan_tier, invoice_number, org_name, exam, expires_days }}
 */
export const createSchoolCheckout = (data) =>
  adminRequest('/api/billing/admin/create-school-checkout', {
    method: 'POST',
    body: JSON.stringify(data),
  });

/**
 * GET /api/billing/admin/invoice/:invoiceNumber
 * Downloads the bilingual Arabic/English PDF invoice for a school payment.
 * Returns a raw Response — caller must call .blob() to get the file.
 *
 * @param {string} invoiceNumber   e.g. "DF-3-20260330120000"
 * @param {object} params          invoice metadata (org_name, exam, plan_tier,
 *                                 student_count, price_per_student, total_sar,
 *                                 expires_days)
 */
export const downloadSchoolInvoice = (invoiceNumber, params = {}) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null))
  ).toString();
  return adminRequest(
    `/api/billing/admin/invoice/${invoiceNumber}${qs ? '?' + qs : ''}`,
    { _raw: true },
  );
};